import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { HOME_GROUPS, canShowNavEntry, type HomeGroupKey } from '@/lib/navigation';
import { useRootLabel } from '@/stores/scope';
import { DOW_LABELS } from '@/lib/calendar-day';
import { HomeAnnouncements } from '@/components/HomeAnnouncements';
import { LineLinkBanner } from '@/components/LineLinkBanner';

/**
 * §7.3 機能カード: L1 + hoverでL2、アイコンは42px角の radius.lg チップに機能グループ色で入れる。
 * グループごとに色と形の両方を変える (§7.3、色覚に依存しない)。
 */
// v0.4 追加要件⑱ D-439: カード定義は lib/navigation.ts へ単一ソース化した (§8-55 の発生源を解消)。
// 画面の追加・説明文・ゲート変更はすべて lib/navigation.ts で行うこと。

// 機能グループ色 (§2.1 group.*)
// アイコン形状 (radius) も変えることで色覚に依存しない差別化 (§7.3)
// §7.3: グループごとに**色と形の両方**を変える (色覚に依存しない差別化)。
// v0.4 追加要件⑱ D-442 のグループ再編に合わせて my / labor を追加、entry / compensation を廃止。
const groupStyles: Record<HomeGroupKey, { iconColor: string; bgColor: string; chipShape: string }> = {
  // 「マイ」= 自分に関する画面。旧「勤怠・給与」の teal をそのまま引き継ぐ
  my:        { iconColor: 'text-group-my',        bgColor: 'bg-group-my-bg',        chipShape: 'rounded-tl-2xl rounded-br-2xl' },
  dashboard: { iconColor: 'text-group-dashboard', bgColor: 'bg-group-dashboard-bg', chipShape: 'rounded-lg' },
  shift:     { iconColor: 'text-group-shift',     bgColor: 'bg-group-shift-bg',     chipShape: 'rounded-full' },
  // 「労務」= 法令・承認まわり。新設の茶系 (白背景とのコントラスト 6.73:1)
  labor:     { iconColor: 'text-group-labor',     bgColor: 'bg-group-labor-bg',     chipShape: 'rounded-br-2xl' },
  // v0.4 使い勝手フィードバック⑤ (D-U7): 「タレントマネジメント」= 配下メンバーのスキル確認。新設プラム系
  talent:    { iconColor: 'text-group-talent',    bgColor: 'bg-group-talent-bg',    chipShape: 'rounded-tr-2xl rounded-bl-2xl' },
  data:      { iconColor: 'text-group-data',      bgColor: 'bg-group-data-bg',      chipShape: 'rounded-md' },
  master:    { iconColor: 'text-group-data',      bgColor: 'bg-group-data-bg',      chipShape: 'rounded-md' },
  menu_dev:  { iconColor: 'text-group-menu',      bgColor: 'bg-group-menu-bg',      chipShape: 'rounded-xl rotate-45' }, // 傾けて菱形
  // v0.5⑧ 経営会議PDCA (D-912): 既存7色 (navy/green/orange/purple/teal/brown/plum) と衝突しない
  // 藍系。既存6グループと異なる形にする (§7-7、色覚に依存しない差別化)
  board:     { iconColor: 'text-group-board',     bgColor: 'bg-group-board-bg',     chipShape: 'rounded-tl-2xl rounded-tr-2xl rounded-br-2xl' },
  admin:     { iconColor: 'text-text-body',       bgColor: 'bg-surface-sunken',     chipShape: 'rounded-lg' },
};

// groupLabel は lib/navigation.ts の NAV_GROUPS[].label を正とするため廃止 (D-439)

// ⚠️ AppLayout.tsx の NavSkeleton と同じ理由 (§8-172): effective_permissions 到着前は
// isAdmin (roles から同期的に判定可能) だけで通せるカードだけが先に表示され、requiredFeature
// 判定のカードは一瞬非表示になる「権限に基づく一部だけの画面」がちらつく (production実機で発見)。
// permsReady が false の間はこのスケルトンを出し、実際のカード集合が確定してから切り替える。
function HomeSkeleton() {
  return (
    <div className="space-y-8" aria-busy="true" aria-label="ホーム読み込み中">
      {[0, 1].map((i) => (
        <div key={i} className="space-y-3">
          <div className="h-3 w-20 rounded bg-surface-line animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[0, 1, 2].map((j) => (
              <div key={j} className="h-[86px] rounded-lg bg-surface-card shadow-card animate-pulse" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function HomePage() {
  const user = useAuthStore((s) => s.user);
  const rootLabel = useRootLabel();
  const permsReady = usePermsReady();
  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  // v0.3 (S-17 staging E2E S15で発覚、AppLayout.tsxのcanShowNavItemと同じロジック):
  // requiredFeatureが定義されたカードはeffective_permissionsベースで判定し、
  // 未定義のカードは従来通りadmin_requiredで判定する
  // v0.4 追加要件⑱ D-439: 表示可否は lib/navigation.ts の canShowNavEntry に一本化した。
  // 従来 AppLayout.canShowNavItem と本ファイルの canShowCard に同じ判定が二重に書かれていた (§8-55)。
  const grouped = HOME_GROUPS
    .map((g) => ({
      group: g.key,
      label: g.label,
      cards: g.items
        .filter((e) => canShowNavEntry(e, user, isAdmin))
        .map((e) => (e.dynamicRootLabel ? { ...e, label: `${rootLabel}サマリ` } : e)),
    }))
    .filter((g) => g.cards.length > 0);

  const today = new Date();
  const dateLabel = `${today.getFullYear()}年${today.getMonth() + 1}月${today.getDate()}日（${DOW_LABELS[today.getDay()]}）`;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* 挨拶 + 日付 (§4.1 page-title) */}
      <div>
        <h1 className="text-[26px] font-black text-text-main -tracking-[0.015em]">
          {user?.name ? `こんにちは、${user.name} さん` : 'ようこそ'}
        </h1>
        <p className="text-[13px] text-text-sub mt-1">
          {dateLabel}
        </p>
      </div>

      {/* v0.4 D-U10 (Phase1): 未読通知の一覧。0件なら何も描画しない */}
      <HomeAnnouncements />

      {/* v0.4 追加要件⑲改訂 (D-844/D-850): LINE未連携の間の連携導線。HomeAnnouncementsより後ろに置き、
          重畳表示時の優先度を下げる (1巡目レビューMajor反映、design-expert) */}
      <LineLinkBanner />

      {!permsReady ? <HomeSkeleton /> : (
        <>
          {grouped.map((group) => (
            <section key={group.group} className="space-y-3">
              {/* §4.1 section 見出し: 11px/800/.1em/text.section */}
              <div className="flex items-center gap-3">
                <h2 className="text-section text-text-section">{group.label}</h2>
                <div className="flex-1 h-px bg-surface-line" aria-hidden />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.cards.map((card) => {
                  const styles = groupStyles[group.group];
                  const Icon = card.icon;
                  return (
                    <Link
                      key={card.to}
                      to={card.to}
                      className="card-press group flex items-start gap-4 p-4 rounded-lg bg-surface-card shadow-card focus-ring"
                    >
                      {/* 42px 角チップ + 機能グループ色。菱形 (rotate-45) は回転前を34px程度に縮めて
                          見かけサイズ (対角線) を他の42px正方形チップと揃える (デザインレビュー反映 2026-08-03) */}
                      <div className="shrink-0 w-[42px] h-[42px] flex items-center justify-center">
                        <div
                          className={`flex items-center justify-center ${styles.bgColor} ${styles.chipShape} ${
                            styles.chipShape.includes('rotate-45') ? 'w-[34px] h-[34px]' : 'w-[42px] h-[42px]'
                          }`}
                        >
                          <div className={styles.chipShape.includes('rotate-45') ? '-rotate-45' : ''}>
                            <Icon size={20} strokeWidth={1.75} className={styles.iconColor} aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-card-title text-text-main">{card.label}</div>
                        <div className="text-[12.5px] text-text-sub mt-1 leading-relaxed line-clamp-2">{card.description}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}

          {grouped.length === 0 && (
            <div className="p-6 text-center text-text-sub rounded-lg bg-surface-card shadow-card">
              利用可能な機能がありません。管理者にお問い合わせください。
            </div>
          )}
        </>
      )}
    </div>
  );
}
